import { memo as Memo, useState, useEffect, useCallback, Fragment, useMemo } from 'react';
import { isBoolean } from 'lodash';

//* HOC's
import withLanguageContext from '@context/consumerHOC/LanguageConsumer';

//* Helpers
import formValidation from '@helpers/formValidation';

//* Components
import Smile from '@components/global/SvgComponents/Smile';
import InputLine from '@components/global/SvgComponents/InputLine';
import FieldErrorMsg from '@components/global/error/FieldErrorMsg';

//* Styles
import InputStyle from './style';

const Input = Memo((props) => {
	//! States
	const [val, setVal] = useState(props.value || '');
	const [type, setType] = useState(props.type === 'c_password' ? 'password' : props.type === 'phone' ? 'number' : props.type === 'link' ? 'text' : props.type || 'text');
	const [errorMsg, setErrorMsg] = useState('');
	const [activeLine, setActiveLine] = useState(false);

	//! Field Value Change
	const onChange = (e) => {
		let newVal = type === 'number' && !e.target.validity.valid ? val : e.target.value;

		setVal(newVal);
		validate(newVal);
		props.onChange && props.onChange(props.name, newVal);
	};

	//! Field Validation
	const validate = useCallback(
		(nextVal) => {
			if (props.realTimeValidate && props.required) {
				const newVal = props.type === 'c_password' ? { pass: props.password, c_pass: nextVal } : nextVal,
					valid = formValidation(newVal, props.type, props.errorMsg, !isBoolean(props.required) && props.required);

				!isBoolean(valid) ? setErrorMsg(valid) : errorMsg && setErrorMsg('');
			}
		},
		[val, props.password, props.parentError]
	);

	//! Error Message
	useEffect(() => {
		setErrorMsg(props.errorMsg);
	}, [props.errorMsg]);

	//! Parent Component Reset
	useEffect(() => {
		if (props.parentVal !== val) {
			setVal(props.parentVal);
			setErrorMsg('');
		}
	}, [props.parentVal]);

	//! Checking Placeholder & Type
	const placeholder = useCallback(props.placeholder ? props.translate(props.placeholder) + (props.required ? '*' : '') : '', [props.selectedLang, props.placeholder, props.required]);

	//! Password Show/Hide Icon Click
	const passIconClick = () => {
		setType(type === 'password' ? 'text' : 'password');
	};

	//! Number Params For Other Browsers
	const numericParams = useMemo(
		() =>
			type === 'number' && {
				pattern: '[0-9]*',
				inputMode: 'numeric',
				min: 0,
				onInput: onChange,
			},
		[type]
	);

	const ActiveLine = useCallback(() => {
		setActiveLine(true);
		setTimeout(() => {
			setActiveLine(false);
		}, 500);
	}, [setActiveLine]);

	const isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);

	return (
		<InputStyle>
			<div className={`input_cont ${errorMsg ? 'error' : ''} ${props.className || ''} ${props.leftIcon ? 'with-left-icon' : ''}`}>
				{props.leftIcon && (typeof props.leftIcon === 'string' ? <i className={`input-left-icon leftIcon`} /> : <i className={`input-left-icon`}>{props.leftIcon}</i>)}

				<input
					type={type}
					name={props.name || null}
					placeholder={placeholder}
					value={val || ''}
					onFocus={ActiveLine}
					onChange={onChange}
					onKeyDown={props.keyDown}
					autoComplete={props.autocomplete ? 'on' : isChrome ? 'chrome-off' : 'off'}
					{...numericParams}
				/>

				{(props.type === 'c_password' || props.type === 'password') && (
					<i
						className={`pass-icon ${type === 'password' ? 'ma-eye-off' : 'ma-eye-on'}`}
						onClick={passIconClick}
					/>
				)}

				{props.validationSmile && (val !== '' || errorMsg) && (
					<Smile
						className={`smile`}
						happy={!(props.errorMsg || errorMsg)}
					/>
				)}

				<InputLine animate={activeLine} />
			</div>

			<FieldErrorMsg msg={props.errorMsg || errorMsg || null} />
		</InputStyle>
	);
});

export default withLanguageContext(Input, ['translate', 'selectedLang']);
